import '@mobiscroll/react/dist/css/mobiscroll.min.css'

import {
  Datepicker as MbscDatepicker,
  localeEnGB,
  localeNl,
  setOptions,
} from '@mobiscroll/react'
import dayjs from 'dayjs'
import i18n from 'i18next'
import { forwardRef, InputHTMLAttributes, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import CalendarIcon from '@/assets/svgs/calendar.svg'
import { ErrorMessage, InputStyled } from '@/components/Input.tsx'
import { Label } from '@/components/Label.tsx'
import { Language } from '~common/constants.ts'
import {
  DATE_INPUT_LENGTH,
  getInitialPickerDate,
  isoDateToDate,
  isoDateToDateInput,
  maskDateInput,
  parseDateInput,
  toIsoDate,
} from '~common/dateInput.ts'

setOptions({
  locale: i18n.language === Language.nl ? localeNl : localeEnGB,
  theme: 'auto',
  themeVariant: 'light',
})

const InputRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  input {
    padding-right: 56px;
  }
`

const PickerButton = styled.button`
  position: absolute;
  right: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border: none;
  background: none;
  cursor: pointer;
`

// Mobiscroll needs an input to attach to; the visible input is our own masked text field
const HiddenPickerInput = forwardRef<HTMLInputElement>((_props, ref) => (
  <input ref={ref} type="hidden" />
))
HiddenPickerInput.displayName = 'HiddenPickerInput'

export const Datepicker = ({
  defaultValue,
  errorMessage,
  invalidMessage,
  label,
  maxValue,
  minValue,
  name,
  onChange,
  placeholder,
  value,
}: {
  name: string
  // ISO dates (YYYY-MM-DD, full timestamps are accepted)
  defaultValue?: string | null
  value?: string | null
  maxValue?: Date
  minValue?: Date
  placeholder?: string
  label?: string
  // Error from the form, shown when the typed text itself is a valid date
  errorMessage?: string
  invalidMessage?: string
  // Called with YYYY-MM-DD, or undefined while the input is empty, incomplete or invalid
  onChange?: (value: string | undefined) => void
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'datepicker' })
  const [text, setText] = useState(() =>
    isoDateToDateInput(value ?? defaultValue),
  )
  const [touched, setTouched] = useState(false)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [anchor, setAnchor] = useState<HTMLDivElement | null>(null)

  const bounds = {
    max: toIsoDate(maxValue || dayjs().toDate()),
    min: toIsoDate(minValue || dayjs().subtract(120, 'years').toDate()),
  }
  const parsed = parseDateInput(text, bounds)

  // Keep the text in sync when a controlled value is changed from outside
  const [syncedValue, setSyncedValue] = useState(value)
  if (value !== syncedValue) {
    setSyncedValue(value)
    if (value && value.slice(0, 10) !== parseDateInput(text)) {
      setText(isoDateToDateInput(value))
    }
  }

  const isComplete = text.length === DATE_INPUT_LENGTH
  const showInvalid = !!text && !parsed && (isComplete || touched)
  const shownError = showInvalid
    ? (invalidMessage ?? t('invalid'))
    : errorMessage

  const updateText = (newText: string) => {
    setText(newText)
    onChange?.(parseDateInput(newText, bounds) ?? undefined)
  }

  const inputProps: InputHTMLAttributes<HTMLInputElement> = {
    autoComplete: 'bday',
    id: name,
    inputMode: 'numeric',
    maxLength: DATE_INPUT_LENGTH,
    name,
    onBlur: () => setTouched(true),
    onChange: (e) => {
      const masked = maskDateInput(e.target.value)
      if (masked.length < DATE_INPUT_LENGTH) {
        setTouched(false)
      }
      updateText(masked)
    },
    placeholder,
    value: text,
  }

  return (
    <>
      <Label htmlFor={name}>{label}</Label>
      <InputRow ref={setAnchor}>
        <InputStyled {...inputProps} invalid={!!shownError} />
        <PickerButton
          type="button"
          aria-label={t('openPicker')}
          onClick={() => setPickerOpen(true)}
        >
          <img src={CalendarIcon} alt="" />
        </PickerButton>
      </InputRow>
      {!!shownError && <ErrorMessage role="alert">{shownError}</ErrorMessage>}
      <MbscDatepicker
        anchor={anchor ?? undefined}
        controls={['date']}
        inputComponent={HiddenPickerInput}
        isOpen={pickerOpen}
        showOnClick={false}
        showOnFocus={false}
        returnFormat="jsdate"
        value={parsed ? isoDateToDate(parsed) : null}
        defaultSelection={isoDateToDate(getInitialPickerDate(bounds))}
        min={isoDateToDate(bounds.min) ?? undefined}
        max={isoDateToDate(bounds.max) ?? undefined}
        circular={true}
        onClose={() => setPickerOpen(false)}
        onChange={(e) => {
          if (e.value instanceof Date) {
            setTouched(false)
            updateText(isoDateToDateInput(toIsoDate(e.value)))
          }
        }}
      />
    </>
  )
}
